import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Alert, Button, Tooltip, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';
import { HrText } from 'shared/ui/Labels';
import { z } from 'zod';
import { compactString } from 'shared/text';
import { doSafe, err, ok, okOrThrow } from 'shared/promise';
import { isNullOrEmpty } from 'shared/collection';
import { zEmailForm, SignInGoogleButton, EmailPassInput, isSignInPath, signInRedirectUrl, emailFormProps, handleCliRedirect, SignInMode } from './SignInCommon';
import { env } from 'utils/env';
import { match, P } from 'ts-pattern';
import { nanoid } from 'nanoid';
import { AuthActionQuery, zAuthActionQuery, zPortalQuery } from 'components/portal/portalTypes';
import { useQueryString } from 'utils/useQueryString';
import { sb } from 'utils/db';
import { HiArrowLeft, HiCheck, HiExclamation } from 'react-icons/hi';
import { useMutation } from '@tanstack/react-query';
import { Auth } from 'api/auth';
import { appName } from 'components/theme/theme';
import { User } from '@supabase/supabase-js';
import { WrapIf } from 'shared/ui';
import { pick } from 'remeda';
import { PrePortalContainer } from 'components/portal/PrePortalContainer';
import { CodeInput } from 'components/CodeInput';
const zQuery = z.object({
  ...pick(zPortalQuery.shape, ['workspace', 'email', 'invite', 'emailInvite']),
  ...pick(zAuthActionQuery.shape, ['authAction']),
  redirect_to: z.string().optional()
});
type SignInState = {
  message?: string;
  mode: SignInMode;
} & ({
  stage: 'form';
} | {
  stage: 'verify-email';
  mode: SignInMode;
  otpType: Auth.VerifyEmailRequest['otpType'];
});
export const SignIn = t0 => {
  const $ = _c(49);
  const {
    error,
    initialMode,
    actionDesc,
    onSignUpRequest,
    onSignUpResponse,
    onSignIn,
    variant: t1,
    hideModeChange
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const router = useRouter();
  const t2 = initialMode ?? "signin";
  let t3;
  if ($[0] !== t2) {
    t3 = {
      mode: t2,
      stage: "form"
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [state, setState] = useState(t3);
  const {
    mode
  } = state;
  const {
    query: q,
    setQuery
  } = useQueryString(zQuery);
  let t4;
  if ($[2] !== q.redirect_to || $[3] !== router) {
    t4 = () => {
      const checkAuthAndRedirect = async () => {
        const {
          data
        } = await sb.auth.getSession();
        if (data.session && q.redirect_to) {
          handleCliRedirect(router, q.redirect_to);
        }
      };
      checkAuthAndRedirect();
    };
    $[2] = q.redirect_to;
    $[3] = router;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== q.redirect_to) {
    t5 = [q.redirect_to];
    $[5] = q.redirect_to;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  const t6 = state.mode == "signin" ? "signin" : "signup";
  let t7;
  if ($[7] !== t6) {
    t7 = emailFormProps(t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const f = useForm(t7);
  let t8;
  if ($[9] !== f || $[10] !== q.email) {
    t8 = () => {
      if (q.email != null && f.values.email == "") {
        f.setFieldValue("email", q.email);
      }
    };
    $[9] = f;
    $[10] = q.email;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== q.email) {
    t9 = [q.email];
    $[12] = q.email;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  useEffect(t8, t9);
  const {
    email
  } = f.values;
  let t10;
  if ($[14] !== email) {
    t10 = zEmailForm.pick({
      email: true
    }).safeParse({
      email
    });
    $[14] = email;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const emailValid = t10.success;
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = React.createRef();
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const formRef = t11;
  const getCurrentUserEnsureAnon = _temp;
  const signUpMutation = useMutation({
    mutationFn: async signUpPropsRaw => {
      sendVerifyEmailMute.reset();
      signInMutation.reset();
      const signUpProps = onSignUpRequest?.(signUpPropsRaw) ?? signUpPropsRaw;
      const {
        email: email_0,
        password
      } = signUpProps;
      const clientSignInAndRedirect = async options => {
        const res = await sb.auth.signInWithPassword({
          email: email_0,
          password
        });
        if (res.error) {
          return err(`${options?.errorTitle ?? "there was a problem signing in"}: ${res.error.message}`);
        }
        if (!res.data.session) {
          return err("unable to sign in (no session unexpectedly)");
        }
        await postSignInRedirect({
          user: res.data.user
        });
        return ok();
      };
      if (mode == "anon-signup") {
        await getCurrentUserEnsureAnon();
      }
      const response = await Auth.signUp(signUpProps);
      onSignUpResponse?.({
        request: signUpProps,
        response
      });
      const result = await match(response).with({
        ok: true
      }, async r => {
        if (r.auto_confirmed) {
          const res_0 = await clientSignInAndRedirect();
          if (!res_0.ok) {
            return res_0;
          }
          return {
            ok: true,
            type: "standard"
          } as const;
        } else {
          setState({
            mode,
            stage: "verify-email",
            message: r.message,
            otpType: "invite"
          });
        }
        if (mode == "anon-signup") {
          if (!r.old_user_id) {
            throw new Error("signup response missing data `old_user_id`");
          }
          if (!r.replacement_token) {
            throw new Error("signup response missing data `replacement_token`");
          }
          return {
            ok: true,
            type: "anon-signup",
            old_user_id: r.old_user_id,
            replacement_token: r.replacement_token
          } as const;
        } else {
          return {
            ok: true,
            type: "standard"
          } as const;
        }
      }).with({
        ok: false,
        errorType: "user-exists"
      }, async r_0 => {
        setState({
          mode: "signin",
          stage: "form"
        });
        const existingUserError = err("User already registered, enter your credentials and sign in");
        if (isNullOrEmpty(password)) {
          return existingUserError;
        }
        return await clientSignInAndRedirect().then(r_1 => r_1.ok ? {
          ok: true,
          type: "standard"
        } as const : existingUserError);
      }).with({
        ok: false
      }, _temp2).exhaustive();
      if (!result.ok) {
        throw new Error(`Sign-up did not complete successfully: ${result.message}`);
      }
      return result;
    }
  });
  const signInMutation = useMutation({
    mutationFn: async t12 => {
      const {
        email: email_1,
        password: password_0
      } = t12;
      sendVerifyEmailMute.reset();
      signUpMutation.reset();
      const {
        error: error_1,
        data: data_1
      } = await sb.auth.signInWithPassword({
        email: email_1,
        password: password_0
      });
      if (error_1) {
        throw new Error(`Unable to sign in: ${error_1.message}`);
      }
      await postSignInRedirect({
        user: data_1.user
      });
    }
  });
  let t13;
  if ($[17] !== mode || $[18] !== q.redirect_to) {
    t13 = {
      mutationFn: async () => {
        const additionalQuery = await match(mode).with("anon-signup", async () => {
          const user = await getCurrentUserEnsureAnon();
          const replacement_token = nanoid();
          await sb.auth.updateUser({
            data: {
              replacement_token
            }
          });
          return {
            authAction: "post-anon-signup",
            auth_old_user_id: user.id,
            auth_replacement_token: replacement_token
          } satisfies AuthActionQuery;
        }).otherwise(_temp3);
        const {
          error: error_2
        } = await sb.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: q.redirect_to ?? signInRedirectUrl(additionalQuery).url
          }
        });
        if (error_2) {
          throw new Error(error_2.message);
        }
      }
    };
    $[17] = mode;
    $[18] = q.redirect_to;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const googleSignInMute = useMutation(t13);
  let t14;
  if ($[20] !== state) {
    t14 = props_0 => {
      setState({
        ...state,
        stage: "verify-email",
        otpType: props_0.otpType
      });
    };
    $[20] = state;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const sendVerifyEmailMute = useMutation({
    mutationFn: async props => {
      signInMutation.reset();
      signUpMutation.reset();
      await Auth.verifyEmail(props).then(okOrThrow);
      return props;
    },
    onSuccess: t14
  });
  let t15;
  if ($[22] !== email || $[23] !== mode || $[24] !== signUpMutation) {
    t15 = async t16 => {
      const {
        code
      } = t16;
      const verify = () => sb.auth.verifyOtp({
        email,
        token: code,
        type: "email"
      } as const).then(_temp4);
      return match(mode).with("anon-signup", async () => {
        if (!signUpMutation.data || signUpMutation.data.type != "anon-signup") {
          throw new Error(`Sign-up did not complete successfully: ${signUpMutation.error}`);
        }
        const {
          old_user_id,
          replacement_token: replacement_token_0
        } = signUpMutation.data;
        const user_0 = await verify();
        await Auth.anonReplacement({
          old_user_id,
          replacement_token: replacement_token_0
        }).then(okOrThrow);
        return user_0;
      }).otherwise(() => verify());
    };
    $[22] = email;
    $[23] = mode;
    $[24] = signUpMutation;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const verifyOtpAndRedirectMute = useMutation({
    mutationFn: t15,
    onSuccess: async user_1 => {
      await postSignInRedirect({
        user: user_1
      });
    }
  });
  let t16;
  if ($[26] !== onSignIn) {
    t16 = {
      mutationFn: async user_2 => {
        if (!onSignIn) {
          return;
        }
        return onSignIn.action(user_2);
      }
    };
    $[26] = onSignIn;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const onSignInMute = useMutation(t16);
  const postSignInRedirect = async t17 => {
    const {
      user: user_3
    } = t17;
    const isCliRedirect = await handleCliRedirect(router, q.redirect_to);
    if (isCliRedirect) {
      return;
    }
    const authAction = match(state).with({
      mode: "signin",
      stage: "verify-email"
    }, () => match(sendVerifyEmailMute.data?.otpType).with("recovery", "magiclink", _temp5).otherwise(_temp6)).otherwise(_temp7);
    if (authAction) {
      setQuery({
        authAction
      });
    }
    if (isSignInPath(router.pathname)) {
      router.replace("/", undefined, {
        shallow: false
      });
    }
    if (onSignIn) {
      onSignInMute.mutate(user_3);
    }
  };
  const [code_0, setCodeRaw] = useState("");
  let t18;
  if ($[28] !== verifyOtpAndRedirectMute) {
    t18 = async code_1 => {
      setCodeRaw(code_1);
      if (code_1.length == 6) {
        verifyOtpAndRedirectMute.mutate({
          code: code_1
        });
      }
    };
    $[28] = verifyOtpAndRedirectMute;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const setCode = t18;
  let t19;
  if ($[30] !== actionDesc || $[31] !== state) {
    let t20;
    if ($[33] !== actionDesc) {
      t20 = () => `Verify your email ${actionDesc ? `to ${actionDesc}` : "to finish signing up"}`;
      $[33] = actionDesc;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] !== actionDesc) {
      t21 = () => `Sign in ${actionDesc ? `to ${actionDesc}` : `to ${appName}`}`;
      $[35] = actionDesc;
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    let t22;
    if ($[37] !== actionDesc) {
      t22 = () => `Sign up ${actionDesc ? `to ${actionDesc}` : `to ${appName}`}`;
      $[37] = actionDesc;
      $[38] = t22;
    } else {
      t22 = $[38];
    }
    t19 = match(state).with({
      stage: "verify-email"
    }, t20).with({
      mode: "signin"
    }, t21).with({
      mode: P.union("signup", "anon-signup")
    }, t22).exhaustive();
    $[30] = actionDesc;
    $[31] = state;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const title = t19;
  const T0 = WrapIf;
  const t20 = variant == "page";
  let t21;
  if ($[39] !== title) {
    t21 = c => <PrePortalContainer title={title}>{c}</PrePortalContainer>;
    $[39] = title;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== state.message) {
    t22 = state.message && <Alert color="primary" className="self-start my-2">{state.message}</Alert>;
    $[41] = state.message;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t25 = match(state).with({
    stage: "verify-email"
  }, t23 => {
    const {
      otpType
    } = t23;
    return <div className="flex flex-col items-start gap-4"><div>We've sent a code to <b>{email}</b>. The code expires shortly, so please enter it soon.</div>{verifyOtpAndRedirectMute.isSuccess ? <div className="flex items-center gap-2 self-center"><HiCheck /><div className="opacity-60 text-lg font-semibold">Email verified</div>{onSignInMute.isPending && <><Loader size="sm" /><div>{onSignIn?.desc}</div></>}</div> : <CodeInput length={6} value={code_0} onChange={code_2 => setCode(code_2)} loading={verifyOtpAndRedirectMute.isPending} mode="num" className="self-center" />}{doSafe(verifyOtpAndRedirectMute.error, _temp8)}<div className="opacity-60 pt-10 flex gap-2 items-center justify-between w-full"><div className="flex gap-2 items-center"><div>Expired or can't find the code?</div><Button component="a" variant="subtle" size="compact-sm" disabled={sendVerifyEmailMute.isPending} loading={sendVerifyEmailMute.isPending} onClick={async () => {
            setCode("");
            verifyOtpAndRedirectMute.reset();
            sendVerifyEmailMute.mutate({
              otpType,
              email
            });
          }}>send a new code</Button></div><Button leftSection={<HiArrowLeft />} onClick={() => setState({
          mode: "signin",
          stage: "form"
        })}>Go back to sign-in</Button></div></div>;
  }).with({
    stage: "form"
  }, () => <div className="flex flex-col w-[30em] max-w-[100vw] self-center"><form className="flex flex-col mt-6 gap-4" ref={formRef} onSubmit={async e_0 => {
      e_0.preventDefault();
      f.validate();
      if (f.isValid()) {
        const password_1 = compactString(f.values.password) ?? nanoid(16);
        await match(mode).with("signup", "anon-signup", async mode_0 => {
          const signUpRequest = {
            email,
            password: password_1,
            ...match({
              ...q,
              mode: mode_0
            }).with({
              mode: "anon-signup"
            }, _temp9).with({
              emailInvite: P.string,
              workspace: P.string
            }, t24 => {
              const {
                emailInvite,
                workspace
              } = t24;
              return {
                mode: "email-invite" as const,
                invite: emailInvite,
                email,
                workspace
              };
            }).with({
              invite: P.string,
              workspace: P.string
            }, _temp10).otherwise(_temp11)
          };
          signUpMutation.mutate(signUpRequest);
        }).with("signin", async () => {
          signInMutation.mutate({
            email,
            password: password_1
          });
        }).exhaustive();
      }
    }}><div className="flex flex-col gap-4"><EmailPassInput size="md" getInputProps={f.getInputProps} mode={match(state.mode).with("anon-signup", _temp12).otherwise(_temp13)} formRef={formRef} /><Button size="md" type="submit" data-test-id="sign-in-email" variant="filled" disabled={signInMutation.isPending || signUpMutation.isPending} loading={signInMutation.isPending || signUpMutation.isPending}>{state.mode == "signup" ? "Sign up" : "Sign in"} with Email</Button>{doSafe(signInMutation.error ?? signUpMutation.error, _temp14)}{state.mode == "signin" && <><Tooltip hidden={emailValid} label={emailValid ? undefined : "enter a valid email first"}><div className="flex items-center justify-between gap-10"><Button variant="outline" loading={sendVerifyEmailMute.isPending} disabled={!emailValid} onClick={() => {
                sendVerifyEmailMute.mutate({
                  otpType: "recovery",
                  email
                });
              }}>reset password</Button><Button variant="outline" loading={sendVerifyEmailMute.isPending} disabled={!emailValid} onClick={async () => {
                sendVerifyEmailMute.mutate({
                  otpType: "magiclink",
                  email
                });
              }}>email one-time code</Button></div></Tooltip></>}</div>{doSafe(sendVerifyEmailMute.error, _temp15)}</form><HrText className="my-4">or</HrText>{!env.NEXT_PUBLIC_SELF_HOST && <SignInGoogleButton loading={googleSignInMute.isPending} onClick={() => googleSignInMute.mutate()}>{state.mode == "signup" ? "Sign up with google" : "Sign in with google"}</SignInGoogleButton>}<ErrorInline title={error} className="mt-4" />{!hideModeChange && <div className="mt-16 flex items-center">{state.mode == "signup" ? "Already have an account?" : "Don't have an account?"}{" "}<Button variant="subtle" onClick={() => {
        setState({
          mode: state.mode == "signup" ? "signin" : "signup",
          stage: "form"
        });
      }}>{state.mode == "signup" ? "sign in" : "sign up"}</Button></div>}</div>).exhaustive();
  let t26;
  if ($[43] !== T0 || $[44] !== t20 || $[45] !== t21 || $[46] !== t22 || $[47] !== t25) {
    t26 = <T0 if={t20} wrap={t21}>{t22}{t25}</T0>;
    $[43] = T0;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  return t26;
};
const ErrorInline = t0 => {
  const $ = _c(4);
  const {
    title,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== title) {
    t1 = title == null && children == null ? null : <Alert variant="light" className={className} icon={<HiExclamation size="2em" />} title={title} color="red">{children}</Alert>;
    $[0] = children;
    $[1] = className;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
async function _temp() {
  const {
    data: data_0,
    error: error_0
  } = await sb.auth.getUser();
  if (error_0) {
    throw new Error(`Unable to get user before verify: ${error_0.message}`);
  }
  if (!data_0.user.is_anonymous) {
    throw new Error("User to be replaced is not anonymous");
  }
  return data_0.user;
}
function _temp2(r_2) {
  return r_2;
}
function _temp3() {
  return {};
}
function _temp4(t0) {
  const {
    error: error_3,
    data: data_3
  } = t0;
  if (error_3) {
    throw new Error(error_3.message);
  }
  return data_3.user;
}
function _temp5(t) {
  return t;
}
function _temp6() {}
function _temp7() {}
function _temp8(e) {
  return <ErrorInline className="mt-4 self-center" title="Unable to replace anonymous user">{e.message}</ErrorInline>;
}
function _temp9(t0) {
  const {
    mode: mode_1
  } = t0;
  return {
    mode: mode_1
  };
}
function _temp10(t0) {
  const {
    invite,
    workspace: workspace_0
  } = t0;
  return {
    mode: "open-invite" as const,
    invite,
    workspace: workspace_0
  };
}
function _temp11() {
  return {
    mode: "sign-up" as const
  };
}
function _temp12() {
  return "signup" as const;
}
function _temp13(m) {
  return m;
}
function _temp14(e_1) {
  return <ErrorInline>{e_1.message}</ErrorInline>;
}
function _temp15(e_2) {
  return <ErrorInline title="Unable to send invitations">{e_2.message}</ErrorInline>;
}